import { ChangeDetectionStrategy, Component, inject, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { SelectUsersListComponent } from '@app/shared/components/select-users-list/select-users-list.component';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { GroupsDataService } from '@app/shared/services/groups-data.service';
import { UsersDataService } from '@app/shared/services/users-data.service';
import { Company } from '@app/shared/types/models/company/company.model';
import { SelectUsersToCompanyGroupDialogData } from '@app/shared/types/models/group/group.model';
import { Project } from '@app/shared/types/models/project/project.model';
import { User } from '@app/shared/types/models/user/user.model';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-select-users-for-company-group-dialog',
  imports: [MatDialogModule, TranslatePipe, UiDialogComponent, SelectUsersListComponent, UiButtonComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './select-users-for-company-group-dialog.component.html'
})
export class SelectUserToCompanyGroupDialogComponent implements OnInit {
  private readonly companyGroupsService: GroupsDataService = inject(GroupsDataService);
  private readonly activeCompanyService: ActiveCompanyService = inject(ActiveCompanyService);
  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);
  private readonly dialogRef: MatDialogRef<SelectUserToCompanyGroupDialogComponent> = inject(
    MatDialogRef<SelectUserToCompanyGroupDialogComponent>
  );
  private readonly usersDataService: UsersDataService = inject(UsersDataService);
  private readonly dialogData: SelectUsersToCompanyGroupDialogData = inject(MAT_DIALOG_DATA);

  protected readonly activeCompany: Signal<Company | undefined> = this.activeCompanyService.activeCompany;
  protected readonly companyUsers: Signal<User[]> = this.usersDataService.getCompanyUsers(this.activeCompany()?.id);
  protected readonly selectedUsers: WritableSignal<string[]> = signal<string[]>(undefined);
  protected readonly preselectedUsers: WritableSignal<string[]> = signal<string[]>(undefined);
  protected readonly lockedUser: WritableSignal<string> = signal<string>(undefined);
  protected readonly selectedProject: Signal<Project> = this.activeProjectService.activeProject;

  protected save(): void {
    const idsToAdd = this.selectedUsers().filter(id => !this.preselectedUsers().includes(id));
    const idsToRemove = this.preselectedUsers().filter(id => !this.selectedUsers().includes(id));

    if (idsToAdd.some(id => this.lockedUser() === id)) {
      idsToAdd.splice(idsToAdd.indexOf(this.lockedUser()), 1);
    }

    if (idsToRemove.some(id => this.lockedUser() === id)) {
      idsToRemove.splice(idsToRemove.indexOf(this.lockedUser()), 1);
    }

    if (idsToAdd.length > 0) {
      this.companyGroupsService.addUsersToGroup(this.activeCompany()?.id, this.dialogData.group, idsToAdd);
    }
    if (idsToRemove.length > 0) {
      this.companyGroupsService.removeUsersFromGroup(this.activeCompany()?.id, this.dialogData.group, idsToRemove);
    }

    this.dialogRef.close();
  }

  public ngOnInit(): void {
    this.preselectedUsers.set(this.dialogData.preselectedUsers.map(user => user.id));
    this.lockedUser.set(this.dialogData.lockedUser?.id);
    this.selectedUsers.set([...this.preselectedUsers()]);
  }
}
